import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { CalendarDays, FileText, Maximize2 } from 'lucide-react';
import { ReportLightbox } from './ReportLightbox';

const REPORTS = [
  {
    kind: 'weekly',
    accent: '#A8D5BA',
    label: 'Every Monday · 08:00',
    title: 'Weekly Brief',
    pages: '2 pages',
    previewImages: ['/landing/weekly_p1.png', '/landing/weekly_p2.png'],
    images: [
      '/landing/weekly_p1.png',
      '/landing/weekly_p2.png',
    ],
    body: 'A focused editorial brief the owner reads with their first chai. Headline verdict, four KPI stat cards, revenue-vs-prior chart, cost composition, day-by-day, channel mix, a week-over-week Movements panel with an AI briefing, and a reconciliation section that\u2019s honest about what the bank data can and cannot prove.',
    bullets: [
      'Executive verdict + revenue-vs-prior chart',
      'Gross margin, EBITDA, Recoverable, Covers KPIs',
      'Week-over-week Movements panel with AI-written briefing',
      'Reconciliation that shows Unverified when bank data is missing',
      'Up to three concrete action items with dispute-by dates',
    ],
    icon: CalendarDays,
  },
  {
    kind: 'monthly',
    accent: '#C9A66B',
    label: 'On the 7th of each month',
    title: 'Monthly Detailed Report',
    pages: '13 pages',
    previewImages: ['/landing/monthly_p1.png', '/landing/monthly_p4.png', '/landing/monthly_p7.png'],
    images: [
      '/landing/monthly_p1.png',
      '/landing/monthly_p2.png',
      '/landing/monthly_p3.png',
      '/landing/monthly_p4.png',
      '/landing/monthly_p5.png',
      '/landing/monthly_p6.png',
      '/landing/monthly_p7.png',
      '/landing/monthly_p8.png',
      '/landing/monthly_p9.png',
      '/landing/monthly_p10.png',
      '/landing/monthly_p11.png',
      '/landing/monthly_p12.png',
      '/landing/monthly_p13.png',
    ],
    body: 'A CFO-grade pack: health check, KPIs, cost breakdown, hospitality ratios, a Movements briefing that reads like the analyst wrote it themselves, daily profit calendar, weekday movement & forecast, recoverable table, supplier watch, payment mix, per-platform aggregator summaries, and a four-page appendix (daily detail · P&L ending at EBITDA with a GST memo · 3-way variance · method notes).',
    bullets: [
      'AI-written Movements briefing over the same numbers on the screen',
      'IFRS-style P&L that ends at EBITDA (with a GST memo)',
      'MoM movement + forecast with ±1σ band',
      'Aggregator platform summary sourced from the canonical settlement lines',
      'Reconciliation with an explicit Unverified state when bank data is not on file',
      'Method-notes appendix so every number is auditable',
    ],
    icon: FileText,
  },
];

// ============================================================
// DeckCarousel — 3D stacked report pages, auto-cycling, click to expand
// ============================================================
const DeckCarousel = ({ images, accent, pages, active, setActive, onOpen, kind, cycleMs }) => {
  const [hovered, setHovered] = useState(false);
  const n = images.length;

  useEffect(() => {
    if (hovered) return;
    const id = setInterval(() => setActive((i) => (i + 1) % n), cycleMs);
    return () => clearInterval(id);
  }, [n, cycleMs, hovered, setActive]);

  const depthOf = (i) => (i - active + n) % n;

  return (
    <div
      className="relative w-full aspect-[3/4] cursor-pointer group/deck"
      style={{ perspective: '1400px', perspectiveOrigin: '50% 30%' }}
      data-testid={`report-deck-${kind}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onOpen}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      aria-label={`Open ${kind} report preview at full size`}
    >
      <div aria-hidden="true" className="pointer-events-none absolute -inset-10 rounded-3xl blur-3xl opacity-40 -z-10 group-hover/deck:opacity-65 transition-opacity duration-500"
        style={{ background: `radial-gradient(circle at 50% 55%, ${accent}, transparent 60%)` }} />

      {images.map((src, i) => {
        const d = depthOf(i);
        const isFront = d === 0;
        const translateZ = -d * 60;
        const translateY = d * 16;
        const rotateX = isFront ? 0 : -3.5;
        const rotateY = isFront ? 0 : (d === 1 ? 6 : -6);
        const scale = (1 - d * 0.04) * (isFront && hovered ? 1.02 : 1);
        const opacity = isFront ? 1 : 0.55 - (d - 1) * 0.18;

        return (
          <motion.div
            key={src}
            initial={false}
            animate={{ translateZ, translateY, rotateX, rotateY, scale, opacity }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformStyle: 'preserve-3d', transformOrigin: 'center center', zIndex: n - d }}
            className="absolute inset-0"
          >
            <div
              className="relative w-full h-full rounded-2xl overflow-hidden border border-white/15 bg-white"
              style={{
                boxShadow: isFront
                  ? `0 32px 80px -20px rgba(0,0,0,0.85), 0 0 0 1px ${accent}33, 0 0 40px -8px ${accent}33`
                  : '0 18px 50px -16px rgba(0,0,0,0.7)',
              }}
            >
              <div className="absolute top-0 left-0 right-0 h-1 z-10" style={{ background: `linear-gradient(90deg, ${accent}, #C9A66B)` }} />
              <img src={src} alt="Report page" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-top" draggable={false} />
              <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-2xl"
                style={{ boxShadow: `inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 ${accent}44` }} />
            </div>
          </motion.div>
        );
      })}

      <div className="absolute top-4 right-4 z-30 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-white bg-black/65 backdrop-blur-md border border-white/20 rounded-full px-3 py-1.5 opacity-0 group-hover/deck:opacity-100 transition-opacity duration-300"
        style={{ transform: 'translateZ(40px)' }}>
        <Maximize2 className="w-3 h-3" />
        Click to expand
      </div>

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3" style={{ transform: 'translate(-50%, 0) translateZ(40px)' }}>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white bg-black/55 backdrop-blur-md border border-white/20 rounded-full px-3 py-1">
          {pages}
        </div>
        <div className="flex items-center gap-1.5 bg-black/55 backdrop-blur-md border border-white/20 rounded-full px-3 py-1.5" data-testid="deck-dots">
          {images.map((_, i) => (
            <button
              key={i} type="button"
              onClick={(e) => { e.stopPropagation(); setActive(i); }}
              data-testid={`deck-dot-${kind}-${i}`}
              aria-label={`Jump to page ${i + 1}`}
              className="block h-1 rounded-full transition-all duration-500 cursor-pointer"
              style={{
                width: i === active ? 16 : 5,
                background: i === active ? accent : 'rgba(255,255,255,0.35)',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// RevolverCard — cards enter edge-on (rotateY -90°), then SNAP into place
// face-on (rotateY 0°) like a revolver chamber clicking into firing position.
// Trigger is driven from a single section-level inView signal (orchestrated)
// so both cards fire deterministically and stay straight forever after.
// ============================================================
const RevolverCard = ({ report, delay, inView }) => {
  const Icon = report.icon;
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const previewImages = report.previewImages || report.images;
  const fullImages = report.images;

  return (
    <div className="relative" style={{ perspective: '1800px', perspectiveOrigin: '50% 50%' }}>
      <motion.div
        initial={{ rotateY: -90, opacity: 0 }}
        animate={inView ? { rotateY: 0, opacity: 1 } : { rotateY: -90, opacity: 0 }}
        transition={{
          rotateY: { duration: 0.85, delay, ease: [0.16, 0.84, 0.44, 1] },
          opacity: { duration: 0.5, delay, ease: 'easeOut' },
        }}
        style={{ transformStyle: 'preserve-3d', transformOrigin: 'center center' }}
        className="group"
        data-testid={`report-preview-${report.kind}`}
      >
        <motion.div
          className="relative mb-10"
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <DeckCarousel
            images={previewImages}
            accent={report.accent}
            pages={report.pages}
            cycleMs={5500 + delay * 4000}
            active={active}
            setActive={setActive}
            onOpen={() => setLightboxOpen(true)}
            kind={report.kind}
          />
        </motion.div>

        <div className="flex items-center gap-3 mb-3">
          <Icon className="w-4 h-4" style={{ color: report.accent }} />
          <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: report.accent }}>
            {report.label}
          </div>
        </div>
        <h3 className="font-display text-white font-semibold text-2xl md:text-3xl tracking-tight mb-3">{report.title}</h3>
        <p className="text-white/60 text-sm md:text-[15px] leading-relaxed mb-5">{report.body}</p>
        <ul className="space-y-2">
          {report.bullets.map((b, idx) => (
            <li key={idx} className="text-white/65 text-sm flex items-start gap-2">
              <span className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: report.accent }} />
              {b}
            </li>
          ))}
        </ul>
      </motion.div>

      <ReportLightbox
        open={lightboxOpen}
        images={fullImages}
        index={active}
        title={report.title}
        accent={report.accent}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

// ============================================================
// Section
// ============================================================
export const ReportPreviews = () => {
  const stageRef = useRef(null);
  // Single deterministic trigger for BOTH revolver cards. Once it fires,
  // it stays true forever (once:true), so the cards lock face-on.
  const inView = useInView(stageRef, { once: true, amount: 0.15 });

  return (
    <section
      id="approach"
      data-testid="landing-reports"
      className="relative py-32 md:py-44 overflow-hidden"
      style={{ backgroundColor: '#0F0B0A' }}
    >
      {/* Feathered top transition from cream LeaksGrid → dark cinema.
          Single clean cream→dark gradient (no via/transparent step) so the seam
          stays smooth and does not produce a light stripe inside the dark section. */}
      <div aria-hidden="true" className="pointer-events-none absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#ECE2C7] to-transparent" />

      {/* Subtle dark grain — keeps the cinema feel from looking flat */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.045] mix-blend-screen"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.3' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* (Ambient gradient blobs removed — section now sits on solid #0F0B0A
          to feel grounded and premium, matching ImpactNumbers / FAQ / ContactCTA.) */}

      <div className="mx-auto max-w-7xl px-6 md:px-10 relative">
        {/* Editorial header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-120px' }}
          transition={{ duration: 0.85 }}
          className="max-w-3xl mb-20 md:mb-28"
        >
          <div className="text-[10px] md:text-xs uppercase tracking-[0.32em] text-[#E8D29A]/85 font-semibold mb-6 flex items-center gap-3">
            <span className="block w-10 h-px bg-[#C9A66B]" />
            Owner-grade Deliverables
          </div>
          <h2 className="font-display text-white font-semibold tracking-tight text-4xl md:text-6xl leading-[1.02]">
            Two reports.
            <span className="block italic bg-clip-text text-transparent bg-gradient-to-r from-[#E8D29A] to-[#C9A66B]">Engineered to be read.</span>
          </h2>
          <p className="text-white/55 text-base md:text-lg mt-6 leading-relaxed">
            The same financial depth, condensed for two different cadences. The Weekly Brief lands every Monday morning. The Monthly Detailed Report arrives on the seventh. Both are designed to be forwarded — to investors, to auditors, to your own team — without a single edit. <span className="text-white/45 italic">Click either deck to view full pages.</span>
          </p>
        </motion.div>

        {/* Stage — subtle horizontal gold line */}
        <div ref={stageRef} className="relative">
          <div aria-hidden="true" className="pointer-events-none absolute left-0 right-0 top-[42%] h-px bg-gradient-to-r from-transparent via-[#C9A66B]/30 to-transparent" />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-20 items-start">
            {/* Weekly clicks in first, Monthly follows ~230ms later — like two revolver chambers cycling into position */}
            <RevolverCard report={REPORTS[0]} delay={0.05} inView={inView} />
            <RevolverCard report={REPORTS[1]} delay={0.28} inView={inView} />
          </div>
        </div>
      </div>

      {/* Bottom feathered fade into next section */}
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0F0B0A] to-transparent" />
    </section>
  );
};

export default ReportPreviews;
